/**
 * Doctor command — diagnose issues
 */

import { readCredentials, isExpired, isAuthenticated } from "../auth/credentials.js";
import { oauthBrowserFlow } from "../auth/oauth.js";
import { detectAllAgents, selectDoctorAgents } from "../agents/detector.js";
import { CREDENTIALS_FILE, SAILFISH_GRAPHQL_URL } from "../constants.js";

export async function runDoctor(): Promise<void> {
  console.log("Sailfish Autofix Doctor\n");
  let issues = 0;

  // 1. Check credentials file
  console.log("1. Checking credentials...");
  const creds = readCredentials();
  if (!creds) {
    console.log("   ❌ No credentials found at " + CREDENTIALS_FILE);
    console.log("   Fix: Run `npx @sailfish-ai/autofix login`");
    issues++;
  } else if (isExpired(creds)) {
    console.log("   ⚠️  Credentials expired — attempting re-authentication...");
    try {
      await oauthBrowserFlow();
      console.log("   ✅ Re-authenticated successfully");
    } catch {
      console.log("   ❌ Re-authentication failed");
      console.log("   Fix: Run `npx @sailfish-ai/autofix login`");
      issues++;
    }
  } else {
    console.log("   ✅ Credentials valid");
  }

  // 2. Check GraphQL connectivity
  console.log("\n2. Checking backend connectivity...");
  if (creds?.jwt) {
    try {
      const resp = await fetch(creds.graphqlUrl || SAILFISH_GRAPHQL_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${creds.jwt}`,
          "X-Sailfish-MCP": "1",
        },
        body: JSON.stringify({ query: "query { __typename }" }),
        signal: AbortSignal.timeout(10_000),
      });

      if (resp.ok) {
        console.log("   ✅ Backend reachable and authenticated");
      } else if (resp.status === 401 || resp.status === 403) {
        console.log(`   ❌ Authentication rejected (HTTP ${resp.status})`);
        console.log("   Fix: Run `npx @sailfish-ai/autofix login`");
        issues++;
      } else {
        console.log(`   ⚠️  Backend returned HTTP ${resp.status}`);
        issues++;
      }
    } catch (err: any) {
      console.log(`   ❌ Cannot reach backend: ${err.message}`);
      console.log(`   Endpoint: ${creds.graphqlUrl}`);
      issues++;
    }
  } else {
    console.log("   ⏭️  Skipped (no credentials)");
  }

  // 3. Check agent installations — only the ones the user set up.
  // Agents that are merely installed on the machine are not findings.
  console.log("\n3. Checking agent installations...");
  const allAgents = detectAllAgents();
  const agents = selectDoctorAgents(allAgents, creds?.agents);
  if (agents.length === 0) {
    console.log("   ⚠️  No agents set up yet");
    const detectedNames = allAgents.filter((a) => a.detected).map((a) => a.name);
    if (detectedNames.length > 0) {
      console.log(`   Detected on this system: ${detectedNames.join(", ")}`);
    }
    console.log("   Fix: Run `npx @sailfish-ai/autofix setup`");
    issues++;
  } else {
    for (const agent of agents) {
      if (!agent.detected) {
        console.log(`   ⚠️  ${agent.name}: was set up but is no longer detected on this system`);
        issues++;
      } else if (!agent.hasSkill || !agent.hasMcp) {
        const missing = [];
        if (!agent.hasSkill) missing.push("skill");
        if (!agent.hasMcp) missing.push("MCP config");
        console.log(`   ⚠️  ${agent.name}: missing ${missing.join(" + ")}`);
        issues++;
      } else {
        console.log(`   ✅ ${agent.name}: fully configured`);
      }
    }
  }

  // Summary
  console.log(`\n${issues === 0 ? "✅ All checks passed!" : `⚠️  ${issues} issue(s) found. Run \`npx @sailfish-ai/autofix setup\` to fix.`}`);
}
